import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import moment from "moment";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";

const DailyProduction = () => {
    const navigate = useNavigate();
    const privileges = secureLocalStorage.getItem("privileges");
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];
    const salesOrderId = searchParams.get("sales_order_id") || "";
    const itemId = searchParams.get("item_id") || "";
    const fromDate = searchParams.get("from_date") || "";
    const toDate = searchParams.get("to_date") || "";
    const hasLinkFilter = Boolean(salesOrderId || itemId || fromDate || toDate);

    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");

    const { useFetch: useDailyPlanList } = useAPIQuery({
        key: "daily-plan",
        endpoint: "/api/daily-plan/",
    });
    const { data, isLoading, isFetching } = useDailyPlanList({
        params: {
            page_no: pageIndex + 1,
            entries: entries,
            q: globalFilterValue !== "" ? globalFilterValue : columnFilterValue,
            c: globalFilterValue === "" ? columnFilterKey : "",
            date: moment(new Date()).format("yyyy-MM"),
            sales_order_id: salesOrderId || undefined,
            item_id: itemId || undefined,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const TypeMasterQuery = useAPIQuery({
        key: "api/type-master",
        endpoint: "/api/type-master/",
    });

    const TypeMasterDeliveryStatus = TypeMasterQuery.useFetch({
        params: {
            q: "Daily Plan Status",
            c: "category",
        },
    });

    const columns = [
        {
            accessor: "wo",
            header: "Work Order Code",
            cell: (row) => <>{row?.wo?.work_order_code}</>,
            filterable: true,
        },
        {
            accessor: "daily_plan",
            header: "Daily plan code",
            cell: (row) => <>{row?.daily_plan_code}</>,
            filterable: true,
        },
        // {
        //     accessor: "daily_production_count",
        //     header: "Daily Production Count",
        // },
        {
            accessor: "item",
            header: "Item",
            cell: (row) => <>{row?.item?.item_name}</>,
            filterable: true,
        },
        {
            accessor: "machine",
            header: "Machine",
            cell: (row) => <>{row?.machine?.item?.item_name}</>,
            filterable: true,
        },
        {
            accessor: "qty_to_be_produced",
            header: "Quantity To Be Produced",
        },
        {
            accessor: "date",
            header: "Date",
        },
        {
            accessor: "activity_status",
            header: "Status",
            cell: (row) => {
                const isOngoing = row?.activity_status === 2;

                return (
                    <span
                        className={`badge ${
                            isOngoing ? "bg-info" : "bg-warning"
                        }`}
                    >
                        {isOngoing ? "On-going" : "Closed"}
                    </span>
                );
            },
            filterable: true,
            filterType: "dropdown",
            filterOpt: TypeMasterDeliveryStatus.data
                ? TypeMasterDeliveryStatus.data?.results?.data?.map((type) => ({
                      label: type?.name,
                      value: type?.value,
                  }))
                : [],
        },
    ];
    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            setGlobalFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                return searchParams;
            });
        }
    };

    const handleColumnFilterChange = (value) => {
        const q = value.map((val) => val.value?.trim() || "").join(",");
        const c = value.map((val) => val.id).join(",");
        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else if (q === "" && c === "") {
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.delete("q");
                searchParams.delete("c");
                return searchParams;
            });
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const clearLinkFilter = () => {
        setSearchParams((searchParams) => {
            ["sales_order_id", "item_id", "from_date", "to_date"].forEach(
                (key) => searchParams.delete(key)
            );
            searchParams.set("page", 1);
            return searchParams;
        });
        setPageIndex(0);
    };

    const firstRow = data?.results?.data?.[0];
    const linkFilterLabel = [
        salesOrderId &&
            `SO ${firstRow?.wo?.sales_order?.so_number || salesOrderId}`,
        itemId && (firstRow?.item?.item_name || `Item ${itemId}`),
        (fromDate || toDate) &&
            `${fromDate || "…"} to ${toDate || "…"}`,
    ]
        .filter(Boolean)
        .join(" · ");

    const handleEditRow = (row) => {
        console.log(row);
        navigate("/daily-production-edit", { state: { data: row.original } });
    };
    console.log(data);
    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Daily Production
                        </h3>
                    </div>
                </div>
                {hasLinkFilter && (
                    <div className="d-flex align-items-center mt-2">
                        <span className="badge bg-light text-dark me-2">
                            Filtered: {linkFilterLabel}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            id="daily-production-clear-link-filter"
                            onClick={clearLinkFilter}
                        >
                            Clear filter
                        </button>
                    </div>
                )}
            </div>
            <Table
                data={data ? data?.results.data : []}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={data ? data.count : 0}
                isLoading={isLoading || isFetching}
                globalSearch
                filterModal={false}
                selectRows={false}
                editRows={
                    privileges.includes("daily_production_edit") ? true : false
                }
                defaultGlobalSearchValue={
                    initialQueries.length > 0 && initialColumns.length <= 0
                        ? initialQueries[0]
                        : undefined
                }
                defaultColumnFiltersValue={
                    initialQueries.length > 0 && initialColumns.length > 0
                        ? initialColumns.map((column, index) => ({
                              id: column,
                              value: initialQueries?.[index] ?? "",
                          }))
                        : undefined
                }
                deleteRows={false}
                onGlobalFilterChange={handleGlobalFilterChange}
                onColumnFilterChange={handleColumnFilterChange}
                onPaginationChange={handlePagination}
                onRowEdit={handleEditRow}
            />
        </>
    );
};

export default DailyProduction;
